/* efx-cfg-diagram.js — T7-M, 2026-09-02. Supersedes the T-DIAGRAM build of 2026-08-31.
 *
 * READ-ONLY companion for the Measurements-step opening drawing, on the same contract as
 * efx-studio-companion.js. It never writes configurator state, never dispatches an event
 * the configurator listens to, never calls a configurator function, and never writes
 * GEOMETRY, OPACITY or FILL to any of the 16 wizard-owned ids inside #efCfgOpeningSvg.
 * It may: append NEW nodes, write TEXT into the two dimension labels, and toggle
 * data-attributes on wrappers. assets/ef-configurator-v2.js is never opened.
 *
 * Rulings implemented (both 2026-09-02, draft #131236954194):
 *   sages/round-7-measure-diagram-design.md  — LOOK   (§3 paint, §3.3 coursing, §3.4 louvre,
 *                                                      §3.6 label chips, §4 echo + the :85 patch)
 *   sages/round-7-measure-diagram-ux.md      — TEACH  (§1 cue inventory, §3 interaction,
 *                                                      §4 echo/format/empty/error, §2 tab swap)
 * Lead resolutions where the two disagree are marked  [R1]..[R5]  at the point of use.
 *
 * WHAT IT BUILDS, in one place:
 *   defs > pattern#efxBrick        true-scale masonry coursing (lines, not filled faces)
 *   g#efxCue                       the whole companion layer, aria-hidden
 *     g#efxPlate                     at-rest objects the teaching rows actually name
 *       rect#efxLintel                 masonry: the steel lintel bar          (UX top-5 #1)
 *       path#efxReveal                 masonry: the face is a plane
 *       rect#efxFlange                 prefab: the trim flange (never measured)
 *       g#efxLouvre > path#efxLouverTop / #efxLouverBot   prefab: recognition
 *       line#efxTrack                  prefab: the existing glass-door track
 *     g#efxCueW / #efxCueH / #efxCueFlangeNot  on demand, exclusive
 *     (#efxCueSq and its two diagonals were deleted by R8-M7 / round-8b B.2 — the owner
 *      retired the Square check row on 2026-09-02 21:30; the rule now lives on the
 *      Inside-fit card in chapter 3, where it is the only place it is true.)
 *   rect#efxDimWChip / #efxDimHChip  ASME-style clear field behind each value
 *
 * Hard no's carried forward from review-4 §F: no <filter>, no patternTransform from CSS,
 * no objectBoundingBox, no stretched bitmap, no flame/logs, no animation of the resize,
 * no wrapper writes to wizard geometry, and the drawing carries no promise and no control.
 */
(function () {
  'use strict';

  var root = document.getElementById('efCfg');
  if (!root) return;
  /* drawScreen() (ef-configurator-v2.js:2704) rewrites svg.innerHTML, which would wipe every
     node injected here. cfg-screen is skipped outright; the CSS hides the diagram too. */
  if (root.dataset && (root.dataset.cfgType === 'cfg-screen' || root.dataset.arch === 'true')) return;
  /* Arch has a separate three-dimension illustration. Do not measure/repaint
     its hidden rectangular SVG. Fraction chips below stay active for all forms. */

  var svg  = document.getElementById('efCfgOpeningSvg');
  var wrap = document.getElementById('efCfgOpeningDiagram');
  var rect = document.getElementById('efCfgOpeningRect');
  var sur  = document.getElementById('efCfgSurroundRect');
  var lblW = document.getElementById('efCfgDimWLabel');
  var lblH = document.getElementById('efCfgDimHLabel');
  if (!svg || !wrap || !rect || !sur || !lblW || !lblH) return;
  if (document.getElementById('efxBrick')) return;          /* already installed */

  var inW  = document.getElementById('efCfgWidth');
  var inH  = document.getElementById('efCfgHeight');
  var NS   = 'http://www.w3.org/2000/svg';

  function node(name, attrs) {
    var e = document.createElementNS(NS, name), k;
    for (k in attrs) if (attrs[k] !== null && attrs[k] !== undefined) e.setAttribute(k, attrs[k]);
    return e;
  }
  function num(el, a) { var v = parseFloat(el && el.getAttribute(a)); return isFinite(v) ? v : NaN; }
  function set(el, attrs) { var k; for (k in attrs) { var v = String(attrs[k]); if (el.getAttribute(k) !== v) el.setAttribute(k, v); } }

  /* ══════════════════════════════════════════════════════════════════════════════════
     1.  MASONRY — drawn coursing, one ground value (design §3.3)
     ══════════════════════════════════════════════════════════════════════════════════
     The whole inch-authored pattern contract from T-DIAGRAM is kept verbatim: viewBox in
     INCHES, patternUnits="userSpaceOnUse", only width/height are ever written, the
     3-module stagger that killed the checkerboard stays. Only the tile's CHILDREN change:
     8 filled pastel faces -> 1 ground rect + 8 joint lines drawn at the true 3/8" joint.
     One value, so T-DIAGRAM §2.3's unresolvable dL* conflict disappears, and the
     "material sample" read the owner rejected becomes structurally impossible. */
  var MODULES = 3, COURSES = 2, MODULE_W = 8, COURSE_H = 8 / 3;
  var TILE_W = MODULES * MODULE_W, TILE_H = COURSES * COURSE_H;
  var MEDIAN_SCALE = 220 / 36;              /* 6.111 u/in, the 36x28" median — the P0 pre-size */
  var GROUND = '#EFECE7', JOINT = '#BDB7AE', JOINT_W = 0.375;

  var pat = node('pattern', {
    id: 'efxBrick', patternUnits: 'userSpaceOnUse',
    viewBox: '0 0 ' + TILE_W + ' ' + TILE_H, preserveAspectRatio: 'none',
    width: TILE_W * MEDIAN_SCALE, height: TILE_H * MEDIAN_SCALE,
    'data-modules': MODULES, 'data-courses': COURSES, 'data-module-inches': MODULE_W
  });
  pat.appendChild(node('rect', { x: 0, y: 0, width: TILE_W, height: TILE_H, fill: GROUND }));
  /* bed joints (2), then head joints per course (3 + 3, offset a half module) */
  [[0, 0, TILE_W, 0], [0, COURSE_H, TILE_W, COURSE_H]].forEach(function (j) {
    pat.appendChild(node('line', { x1: j[0], y1: j[1], x2: j[2], y2: j[3], stroke: JOINT, 'stroke-width': JOINT_W }));
  });
  [0, 8, 16].forEach(function (x) {
    pat.appendChild(node('line', { x1: x, y1: 0, x2: x, y2: COURSE_H, stroke: JOINT, 'stroke-width': JOINT_W }));
  });
  [4, 12, 20].forEach(function (x) {
    pat.appendChild(node('line', { x1: x, y1: COURSE_H, x2: x, y2: TILE_H, stroke: JOINT, 'stroke-width': JOINT_W }));
  });

  /* OWNER, 2026-09-02 17:55 PT (references/owner-imagery-2026-09-01/measure-fireplace-types/
     README.md): "masonry wyglad z lintel bar to bardzo cienki metal about 1/2-3/4"".
     The same two photographs show the other half of the masonry diagnostic: the INSIDE of a
     masonry firebox is FIREBRICK, in large courses, and it is visible in every masonry photo
     the owner sent. It is drawn at the same 7% ink as the back-wall hint, so it is a
     recognition cue and never competes with the W/H read (review-4 F5's real constraint was
     "no noise inside the firebox"; a 7% coursing line is not noise, and the owner's input
     overrides the sages where they conflict). Nominal firebrick: 9" x 4.5" course. */
  var FB_W = 9, FB_H = 4.5, FBT_W = 2 * FB_W, FBT_H = 2 * FB_H;
  var fbPat = node('pattern', {
    id: 'efxFireBrick', patternUnits: 'userSpaceOnUse',
    viewBox: '0 0 ' + FBT_W + ' ' + FBT_H, preserveAspectRatio: 'none',
    width: FBT_W * MEDIAN_SCALE, height: FBT_H * MEDIAN_SCALE,
    'data-module-inches': FB_W
  });
  [[0, 0, FBT_W, 0], [0, FB_H, FBT_W, FB_H],
   [0, 0, 0, FB_H], [FB_W, 0, FB_W, FB_H],
   [FB_W / 2, FB_H, FB_W / 2, FBT_H], [FB_W * 1.5, FB_H, FB_W * 1.5, FBT_H]]
    .forEach(function (j) {
      fbPat.appendChild(node('line', { x1: j[0], y1: j[1], x2: j[2], y2: j[3], 'stroke-width': 0.5 }));
    });

  var defs = node('defs', {});
  defs.appendChild(pat);
  defs.appendChild(fbPat);
  svg.appendChild(defs);
  wrap.setAttribute('data-efx-brick', 'on');   /* the CSS references url(#efxBrick) only once this is set */

  /* ══════════════════════════════════════════════════════════════════════════════════
     2.  THE CUE LAYER (UX §1)
     ══════════════════════════════════════════════════════════════════════════════════
     Six of the nine objects the teaching rows name were not drawn at all — no lintel, no
     flange, no louvers, no track, no three-point strings, no diagonals. A premium technical
     drawing is one whose callouts have referents. Everything here is a NEW node in a
     companion-owned <g>; nothing writes to the wizard's 16 ids.
     aria-hidden: every fact the layer carries is already in the row button's own text, so a
     screen-reader user loses nothing and gains no noise (UX §3, and review-4 §F: the drawing
     carries no promise and no control — zero focusable nodes). */
  var cue = node('g', { id: 'efxCue', 'aria-hidden': 'true' });

  var plate = node('g', { id: 'efxPlate' });
  var lintel = node('rect', { id: 'efxLintel', x: 0, y: 0, width: 0, height: 0 });
  var reveal = node('path', { id: 'efxReveal', d: 'M0 0', fill: 'none' });
  var fbrick = node('rect', { id: 'efxFireBrickRect', x: 0, y: 0, width: 0, height: 0, fill: 'url(#efxFireBrick)' });
  /* #efxFlange is the BLACK METAL BOX FACE (owner: "zero clearance = a BLACK METAL BOX"),
     drawn as a band on the opening's own outline. #efxFlangeLip is the thin trim lip at that
     band's outer edge - the "thin black lip around it" the README names, and the thing the
     buyer must never include in his width. */
  var flange = node('rect', { id: 'efxFlange', x: 0, y: 0, width: 0, height: 0, fill: 'none' });
  var flip   = node('rect', { id: 'efxFlangeLip', x: 0, y: 0, width: 0, height: 0, fill: 'none' });
  var louvre = node('g', { id: 'efxLouvre' });
  var louvT  = node('path', { id: 'efxLouverTop', d: 'M0 0', 'data-slots': '0' });
  var louvB  = node('path', { id: 'efxLouverBot', d: 'M0 0', 'data-slots': '0' });
  var track  = node('line', { id: 'efxTrack', x1: 0, y1: 0, x2: 0, y2: 0 });
  louvre.appendChild(louvT); louvre.appendChild(louvB);
  /* z-order inside the plate: flange and louvers sit on the surround, the reveal and the
     lintel sit on the opening, the track last so it reads as hardware in front. */
  plate.appendChild(fbrick);
  plate.appendChild(flange); plate.appendChild(flip); plate.appendChild(louvre);
  plate.appendChild(lintel); plate.appendChild(reveal); plate.appendChild(track);
  cue.appendChild(plate);

  /* --- the on-demand cues. Exclusive: exactly one of width|height|flange.
         UX §1 arithmetic: three 18u strings inside a 133 CSS px firebox occupy 44% of it, so
         they may never be at rest. -------------------------------------------------------- */
  function string(axis, i) {
    var g = node('g', { 'data-axis': axis, 'data-i': i, class: 'efx-cue-string' });
    g.appendChild(node('line', { class: 'efx-cue-line', x1: 0, y1: 0, x2: 0, y2: 0 }));
    g.appendChild(node('line', { class: 'efx-cue-cap', x1: 0, y1: 0, x2: 0, y2: 0 }));
    g.appendChild(node('line', { class: 'efx-cue-cap', x1: 0, y1: 0, x2: 0, y2: 0 }));
    return g;
  }
  var cueW = node('g', { id: 'efxCueW' });
  var cueH = node('g', { id: 'efxCueH' });
  var wStr = [0, 1, 2].map(function (i) { var g = string('w', i); cueW.appendChild(g); return g; });
  var hStr = [0, 1, 2].map(function (i) { var g = string('h', i); cueH.appendChild(g); return g; });
  /* #efxCueHCapTop is the top cap of the CENTRE height string: it is the node that lands on
     the lintel's underside, which is the whole of the lintel rule (UX gate 11). */
  hStr[1].querySelector('.efx-cue-cap').setAttribute('id', 'efxCueHCapTop');

  /* The only "not this" mark in the system. It earns its place because a flange-inclusive
     width is a plausible number, so no downstream range check can ever flag it (UX §1). */
  var cueFN = node('g', { id: 'efxCueFlangeNot' });
  cueFN.appendChild(node('rect', { id: 'efxFlangeNotBox', x: 0, y: 0, width: 0, height: 0, fill: 'none' }));
  cueFN.appendChild(node('line', { id: 'efxFlangeNotStrike', x1: 0, y1: 0, x2: 0, y2: 0 }));

  cue.appendChild(cueW); cue.appendChild(cueH); cue.appendChild(cueFN);
  svg.appendChild(cue);

  /* --- label chips (design §3.6). ASME Y14.5 puts the value in a break in the dimension
         line; breaking the line would need x1/x2 writes on wizard nodes, so the chip gives
         the value its clear field inside the constraint. Declared as a deviation.
         Inserted BEFORE the label in its own <g> so it paints behind it. drawOpening() only
         setAttribute()s inside those groups — it never rewrites them — so this is stable. */
  var chipW = node('rect', { id: 'efxDimWChip', x: 0, y: 0, width: 0, height: 0, rx: 2 });
  var chipH = node('rect', { id: 'efxDimHChip', x: 0, y: 0, width: 0, height: 0, rx: 2 });
  if (lblW.parentNode) lblW.parentNode.insertBefore(chipW, lblW);
  if (lblH.parentNode) lblH.parentNode.insertBefore(chipH, lblH);

  /* ══════════════════════════════════════════════════════════════════════════════════
     3.  VALUE PARSING, FORMAT AND THE GLYPH GATE (UX §4)
     ══════════════════════════════════════════════════════════════════════════════════ */
  /* One character in both places. DM Sans's coverage of U+2033 could not be verified off
     line, so it is gated at runtime: true -> the double prime everywhere, false -> the
     straight mark everywhere, INCLUDING the precision pill, which is rewritten below.
     A mark that falls back to a different typeface mid-string is invisible in 13 px prose
     and glaring in a 15 px drawing callout beside tabular figures. */
  var MARK = '″';
  try {
    if (document.fonts && document.fonts.check && !document.fonts.check('600 14px "DM Sans"', '″')) MARK = '"';
  } catch (e) {}

  function parseIn(v) {
    if (v === null || v === undefined) return NaN;
    v = String(v).trim().replace(/["″”]/g, '');
    if (!v) return NaN;
    var m = v.match(/^(\d+(?:\.\d+)?)\s+(\d+)\/(\d+)$/);
    if (m) return parseFloat(m[1]) + parseInt(m[2], 10) / parseInt(m[3], 10);
    m = v.match(/^(\d+(?:\.\d+)?)$/);
    if (m) return parseFloat(m[1]);
    m = v.match(/^(\d+)\/(\d+)$/);
    if (m) return parseInt(m[1], 10) / parseInt(m[2], 10);
    return NaN;
  }
  /* Never silently round to 1/8: the pill ASKS for 1/8, it does not promise we will invent
     it. An exact eighth prints as a mixed fraction with a plain 1/8 (never the precomposed
     glyph — mixing U+215B with 1/16 looks broken); anything else prints to 2 dp. */
  function fmtIn(n) {
    if (!isFinite(n) || n <= 0) return '';
    var whole = Math.floor(n + 1e-9), frac = n - whole;
    var eighths = Math.round(frac * 8);
    if (Math.abs(frac * 8 - eighths) < 1e-6) {
      if (eighths === 8) { whole += 1; eighths = 0; }
      if (!eighths) return whole + MARK;
      var den = 8, numr = eighths;
      while (numr % 2 === 0 && den % 2 === 0) { numr /= 2; den /= 2; }
      return whole + ' ' + numr + '/' + den + MARK;
    }
    return (Math.round(n * 100) / 100).toFixed(2).replace(/0$/, '').replace(/\.$/, '') + MARK;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════
     4.  THE ECHO (design §4, UX §4) — with the paired parser patch, which is not optional
     ══════════════════════════════════════════════════════════════════════════════════
     drawOpening() prints Math.round(w) (ef-configurator-v2.js:3472 passes the rounded int),
     so a typed 36 1/8 renders as 36" and the drawing contradicts the field the buyer just
     filled, on the one chapter whose job is to prove we listened. The ORDER always stored
     36.125; only the display was wrong. This is the only honest fix that does not touch
     frozen JS.
     THE PAIRED PATCH: sync() below reads dataset.efxIn FIRST. The old line derived the
     brick scale from textContent.replace(/[^0-9.]/g,''), which on "36 1/8" yields 3618 and
     the masonry texture explodes. Shipping the echo without this is a regression. */
  var lastWizard = { w: 36, h: 28 };   /* the last number the wizard itself printed */
  var obsW = null, obsH = null;

  function stateFor(input) {
    var raw = input ? String(input.value || '').trim() : '';
    if (!raw) return { kind: 'empty' };
    var n = parseIn(raw);
    /* The wizard redraws only >= 10" (js:3470) and marks the suffix .is-valid on the same
       threshold. A 4" opening drawn to scale is nonsense; drawing it is a worse lie than
       not drawing it, and the message stays at the FIELD — the plate is aria-hidden, so an
       error stated inside it is unreachable (UX §4). */
    if (!isFinite(n) || n < 10) return { kind: 'invalid' };
    return { kind: 'value', n: n, text: fmtIn(n) };
  }

  function writeLabel(lbl, obs, desired, inches) {
    if (lbl.textContent !== desired) {
      if (obs) obs.disconnect();
      lbl.textContent = desired;
      if (obs) obs.observe(lbl, { childList: true, characterData: true, subtree: true });
    }
    var d = isFinite(inches) && inches > 0 ? String(inches) : '';
    if (lbl.dataset.efxIn !== d) lbl.dataset.efxIn = d;
  }

  function echo() {
    var sw = stateFor(inW), sh = stateFor(inH);
    /* Remember whatever number the wizard last printed, so the brick scale still has a true
       inch value in the empty state (the drawing is at the wizard's own 36x28 default). */
    var wizW = parseFloat(String(lblW.textContent || '').replace(/[^0-9.]/g, ''));
    var wizH = parseFloat(String(lblH.textContent || '').replace(/[^0-9.]/g, ''));
    if (isFinite(wizW) && wizW >= 10 && sw.kind !== 'value') lastWizard.w = wizW;
    if (isFinite(wizH) && wizH >= 10 && sh.kind !== 'value') lastWizard.h = wizH;

    /* Empty state (UX §3/#3, gate 1): before a keystroke the drawing must not assert a size
       we do not have — and the hard-coded 36x28 was wrong for every product that is not
       36x28. W / H, dimension rules at the muted value. */
    writeLabel(lblW, obsW, sw.kind === 'value' ? sw.text : 'W', sw.kind === 'value' ? sw.n : lastWizard.w);
    writeLabel(lblH, obsH, sh.kind === 'value' ? sh.text : 'H', sh.kind === 'value' ? sh.n : lastWizard.h);
    if (wrap.dataset.efxW !== sw.kind) wrap.dataset.efxW = sw.kind;
    if (wrap.dataset.efxH !== sh.kind) wrap.dataset.efxH = sh.kind;
    chips();
  }

  /* The chip is re-measured on every label mutation so it tracks a longer string like
     "36 1/8"". Geometry is written ONLY on our own two rects; the wizard's x/y are read. */
  function chip(lbl, box) {
    var bb;
    try { bb = lbl.getBBox(); } catch (e) { return; }
    if (!bb || !bb.width) { set(box, { width: 0, height: 0 }); return; }
    set(box, { x: bb.x - 5, y: bb.y - 2, width: bb.width + 10, height: bb.height + 4 });
    var t = lbl.getAttribute('transform');
    if (t) box.setAttribute('transform', t); else box.removeAttribute('transform');
  }
  function chips() { chip(lblW, chipW); chip(lblH, chipH); }

  /* ══════════════════════════════════════════════════════════════════════════════════
     5.  BRICK SCALE — the T-DIAGRAM contract, now reading the echo's decimal first
     ══════════════════════════════════════════════════════════════════════════════════ */
  function inchesW() {
    var v = parseFloat(lblW.dataset.efxIn || '');
    if (isFinite(v) && v > 0) return v;
    v = parseFloat(String(lblW.textContent || '').replace(/[^0-9.]/g, ''));
    return isFinite(v) && v > 0 ? v : NaN;
  }
  function scale() {
    var oW = num(rect, 'width'), wIn = inchesW();
    return (oW > 0 && wIn > 0) ? oW / wIn : NaN;
  }
  function syncBrick() {
    var s = scale();
    if (!(s > 0)) return;
    set(pat, { width: (TILE_W * s).toFixed(3), height: (TILE_H * s).toFixed(3) });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════
     6.  WHICH PLATE  (UX §2 — the tab swaps the plate, with a visible state marker)
     ══════════════════════════════════════════════════════════════════════════════════
     A caption reading "inside the metal frame" over a picture of brick is worse than the
     switch it was avoiding. The tab is the plate's authority; efx-studio-companion.js
     already sets that tab from chapter 1 until the buyer overrides it, so in the normal
     case the plate still follows chapter 1 exactly as before.
     [R3] data-efx-type is the ONE branch key: the design ruling's own §3.4 toggle derived it
     from the wizard's fill string, which cannot express the diverged state. Same attribute,
     one source.  Everything downstream (louvre display, flange, surround repaint,
     visibility on mantel/hearth) keys off it in CSS. */
  var tabPre = document.getElementById('efxMmPrefab');
  var divNote = document.getElementById('efxDivergeNote');

  function chapterOneType() {
    var g = root.querySelector('.ef-cfg-group[data-group="fireplace_type"]');
    if (!g) return '';
    var card = g.querySelector('.ef-cfg-card[aria-checked="true"]');
    if (!card) return '';
    var t = card.querySelector('.ef-cfg-card__title');
    var v = ((t ? t.textContent : card.textContent) || '').toLowerCase();
    if (/masonry|brick|stone/.test(v)) return 'masonry';
    if (/prefab|factory|metal|zero.?clearance/.test(v)) return 'prefab';
    return '';
  }
  function syncType() {
    var shown = (tabPre && tabPre.checked) ? 'prefab' : 'masonry';
    if (wrap.dataset.efxType !== shown) wrap.dataset.efxType = shown;
    var mine = chapterOneType();
    var diverged = !!mine && mine !== shown;
    if (divNote) {
      /* Server-rendered both ways; a read-only listener flips one `hidden`. The T5-10 §6.6
         pattern already in use for .efx-cfg-fitlines. */
      if (divNote.hidden !== !diverged) divNote.hidden = !diverged;
      var lbl = divNote.querySelector('[data-diverge-shown]');
      if (lbl) {
        var txt = shown === 'prefab' ? 'Factory-built' : 'Masonry';
        if (lbl.textContent !== txt) lbl.textContent = txt;
      }
      var own = divNote.querySelector('[data-diverge-mine]');
      if (own) {
        var t2 = mine === 'prefab' ? 'Factory-built' : 'Masonry';
        if (own.textContent !== t2) own.textContent = t2;
      }
    }
    /* DIRECT-1 (3 IX): the matching-state note is gone (tiles replaced the tabs); never let this
       selector land on the diverge note itself, which would un-hide it while the types agree. */
    var noteMatch = document.querySelector('.efx-measure-teach__note:not(#efxDivergeNote)');
    if (noteMatch && noteMatch !== divNote) noteMatch.hidden = diverged;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════
     7.  LAYOUT — every companion node derived from the wizard's own current geometry
     ══════════════════════════════════════════════════════════════════════════════════
     Read-only inputs: #efCfgOpeningRect x/y/width/height, #efCfgSurroundRect x/y/width/height,
     and the scale implied by the dimension label. Nothing here writes to either. */
  var LOUVER_PITCH = 3.5;          /* user units; ~1.6% of a 220 u opening = the photograph's
                                      own slot pitch on _reboot/assets/img/efx-router-doors-prefab.webp */
  var LOUVER_MIN = 14, LOUVER_MAX = 48;

  function slotPath(x, y, w, h) {
    /* One <path> per band instead of N <line>s. At the true 3.5 u pitch a 232 u band is 66
       slots; two bands as elements would put #efCfgOpeningSvg past the 120-node budget
       (design G10) on their own. The count is published as data-slots so the gate can still
       assert it. Declared deviation from design G3's literal element count. */
    var inset = 10, x0 = x + inset, span = w - inset * 2;
    if (span <= 0 || h <= 0) { return { d: 'M0 0', n: 0 }; }
    var n = Math.max(LOUVER_MIN, Math.min(LOUVER_MAX, Math.round(span / LOUVER_PITCH)));
    var step = span / (n - 1), d = '', i;
    for (i = 0; i < n; i++) { var px = (x0 + i * step).toFixed(2); d += 'M' + px + ' ' + y.toFixed(2) + 'V' + (y + h).toFixed(2); }
    return { d: d, n: n };
  }

  function layout() {
    var x0 = num(rect, 'x'), y0 = num(rect, 'y'), w0 = num(rect, 'width'), h0 = num(rect, 'height');
    if (!(w0 > 0) || !(h0 > 0)) return;
    var sx = num(sur, 'x'), sy = num(sur, 'y'), sw = num(sur, 'width'), sh = num(sur, 'height');
    var s = scale(); if (!(s > 0)) s = w0 / 36;
    var recessed = wrap.dataset.lintel === 'recessed';

    /* --- lintel. OWNER 2026-09-02, with photographs: the bar is "bardzo cienki metal about
           1/2-3/4"" - a thin steel ANGLE whose visible leg is 5/8", not the 3.5" the design
           sage proposed unmeasured. Drawn as a hairline steel edge at the top of the opening,
           floored at 2 u so it survives a small drawing and capped at 6 u so it can never
           read as a header band. */
    var lh = Math.max(2, Math.min(0.625 * s, 6));
    var slide = recessed ? 1 * s : 0;            /* the >1" case: the bar sits back, brick shows above it */
    set(lintel, { x: x0, y: y0 + slide, width: w0, height: lh });

    /* --- reveal: top and both sides, 1 u inside the opening. One line that states "the face
           is a plane and the firebox is behind it". */
    var r = 1;
    set(reveal, { d: 'M' + (x0 + r) + ' ' + (y0 + h0) + 'V' + (y0 + r) + 'H' + (x0 + w0 - r) + 'V' + (y0 + h0) });

    /* --- the black metal box face. A band OUTSIDE the opening on all four sides, drawn as a
           stroke on the band's centreline so its bbox strictly contains the opening (UX gate
           13) without painting over the firebox. WeLoveFire's "distance from exterior frame
           to door opening" IS this band, and it must never enter the field. */
    var fb = Math.max(6, Math.min(3 * s, 26));
    set(flange, { x: x0 - fb / 2, y: y0 - fb / 2, width: w0 + fb, height: h0 + fb, 'stroke-width': fb });
    set(flip,   { x: x0 - fb, y: y0 - fb, width: w0 + 2 * fb, height: h0 + 2 * fb });

    /* --- firebrick, inside the opening, masonry only. */
    set(fbrick, { x: x0, y: y0, width: w0, height: h0 });
    set(fbPat, { width: (FBT_W * s).toFixed(3), height: (FBT_H * s).toFixed(3) });

    /* --- louvers. OWNER: some zero-clearance boxes have louvers top AND bottom, some have
           NONE. So they sit ON the box face (not in the surround gap, which is 0 below a
           flush hearth), at reduced ink, as a SECONDARY cue - the constant is the black box
           and its lip. The caption says so in words. */
    var lb = Math.max(4, Math.min(fb - 5, 16));
    var bandX = x0 - fb + 3, bandW = w0 + 2 * fb - 6;
    var tOK = lb >= 4, bOK = lb >= 4;
    var pt = tOK ? slotPath(bandX, y0 - fb + 2.5, bandW, lb) : { d: 'M0 0', n: 0 };
    var pb = bOK ? slotPath(bandX, y0 + h0 + fb - lb - 2.5, bandW, lb) : { d: 'M0 0', n: 0 };
    set(louvT, { d: pt.d, 'data-slots': pt.n });
    set(louvB, { d: pb.d, 'data-slots': pb.n });
    set(louvre, { 'data-bands': (pt.n ? 1 : 0) + (pb.n ? 1 : 0) });

    /* --- track: the existing glass-door channel, 2 u inside the opening's top edge. */
    set(track, { x1: x0 + 2, y1: y0 + 2, x2: x0 + w0 - 2, y2: y0 + 2 });

    /* --- width cue: three horizontal strings at 10 / 50 / 90 % of the opening height. */
    [0.10, 0.50, 0.90].forEach(function (f, i) {
      var y = y0 + f * h0, g = wStr[i], kid = g.children;
      set(kid[0], { x1: x0, y1: y, x2: x0 + w0, y2: y });
      set(kid[1], { x1: x0, y1: y - 4, x2: x0, y2: y + 4 });
      set(kid[2], { x1: x0 + w0, y1: y - 4, x2: x0 + w0, y2: y + 4 });
    });

    /* --- height cue: three vertical strings at 10 / 50 / 90 % of the opening width. The
           CENTRE string's top cap lands on the lintel's underside — that is the rule. */
    var capY = y0 + lh + slide;
    [0.10, 0.50, 0.90].forEach(function (f, i) {
      var x = x0 + f * w0, g = hStr[i], kid = g.children;
      var top = (i === 1) ? capY : y0;
      set(kid[0], { x1: x, y1: top, x2: x, y2: y0 + h0 });
      set(kid[1], { x1: x - 4, y1: top, x2: x + 4, y2: top });
      set(kid[2], { x1: x - 4, y1: y0 + h0, x2: x + 4, y2: y0 + h0 });
    });

    /* --- flange-not: a bracket across the flange with a strike. */
    var fnY = y0 + h0 * 0.5;
    set(cueFN.children[0], { x: x0 - fb, y: fnY - fb * 0.8, width: fb, height: fb * 1.6 });
    set(cueFN.children[1], { x1: x0 - fb - 2, y1: fnY + fb * 0.9, x2: x0 + 2, y2: fnY - fb * 0.9 });

    chips();
  }

  /* ══════════════════════════════════════════════════════════════════════════════════
     8.  THE CUE CONTROLLER  (UX §3)
     ══════════════════════════════════════════════════════════════════════════════════
     Hover/focus set .cueHover, click pins .cue. cueHover beats cue while set; on
     mouseleave/blur the pinned cue restores. Precedence lives in ONE CSS chain — no JS
     state machine. Escape clears both (WCAG 1.4.13 dismissible).
     1.4.13 "hoverable" is satisfied trivially and deliberately: the cue is a separate region
     that never overlays its trigger, so there is nothing for the pointer to travel onto. */
  var rows = root.querySelectorAll('.efx-mrow__btn');
  var canHover = true;
  try { canHover = window.matchMedia('(hover:hover) and (pointer:fine)').matches; } catch (e) {}

  function setHover(v) { if (v) wrap.dataset.cueHover = v; else delete wrap.dataset.cueHover; }
  function setPin(v) {
    if (v) wrap.dataset.cue = v; else delete wrap.dataset.cue;
    for (var i = 0; i < rows.length; i++) {
      var c = rows[i].parentNode.getAttribute('data-cue');
      rows[i].setAttribute('aria-pressed', (v && c === v) ? 'true' : 'false');
    }
  }
  function clearAll() { setHover(''); setPin(''); }

  Array.prototype.forEach.call(rows, function (btn) {
    var c = btn.parentNode.getAttribute('data-cue');
    if (!c) return;
    if (canHover) {
      btn.addEventListener('mouseenter', function () { setHover(c); });
      btn.addEventListener('mouseleave', function () { setHover(''); });
    }
    /* Keyboard parity is required, not optional: WCAG 1.4.13 — content triggerable by
       pointer hover must also be triggerable by keyboard focus. */
    btn.addEventListener('focus', function () { setHover(c); });
    btn.addEventListener('blur', function () { setHover(''); });
    btn.addEventListener('click', function () {
      var pinned = wrap.dataset.cue === c;
      /* Second tap on the Height row toggles the lintel case. Two static cross-sections is
         what the market ships and a novice cannot tell which one is his; one node that
         moves teaches. */
      if (pinned && c === 'height') {
        wrap.dataset.lintel = (wrap.dataset.lintel === 'recessed') ? 'proud' : 'recessed';
        layout();
        return;
      }
      setPin(pinned ? '' : c);
      btn.parentNode.classList.toggle('is-open', !pinned);
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') clearAll();
  });
  /* The field and its rule become the same object while the buyer is in the field. */
  if (inW) { inW.addEventListener('focus', function () { setHover('width'); }); inW.addEventListener('blur', function () { setHover(''); }); }
  if (inH) { inH.addEventListener('focus', function () { setHover('height'); }); inH.addEventListener('blur', function () { setHover(''); }); }

  /* Tab switch clears the pinned cue — the old cue belonged to the old plate — and must not
     touch the opening geometry, the two values or any input (UX §3, gate 9). */
  ['efxMmMasonry', 'efxMmPrefab'].forEach(function (id) {
    var el = document.getElementById(id);
    if (el) el.addEventListener('change', function () { clearAll(); syncType(); layout(); });
  });
  var backLink = document.getElementById('efxDivergeBack');
  if (backLink) backLink.addEventListener('click', function (e) {
    e.preventDefault();
    var mine = chapterOneType();
    var t = document.getElementById(mine === 'prefab' ? 'efxMmPrefab' : 'efxMmMasonry');
    if (t && !t.checked) { t.checked = true; clearAll(); syncType(); layout(); }
  });

  /* ══════════════════════════════════════════════════════════════════════════════════
     9.  MOBILE ECHO BAR (UX §6) — injected, not authored in the snippet
     ══════════════════════════════════════════════════════════════════════════════════
     At 390 the plate is 300x253 and the keyboard-up viewport is ~508 px, so a sticky plate
     hides the field being typed into and a shrunk plate scales the 18 u callout to ~5 CSS px.
     Focus-swap instead: the plate steps aside for a 44 px numeric bar while a field has
     focus, and returns on blur. The SWAP is pure CSS (:focus-within); only the text is JS.
     This is a focus state, not a scroll hide, so it does not touch the transform-only rule. */
  var echoBar = null;
  if (wrap.parentNode) {
    echoBar = document.createElement('div');
    echoBar.className = 'efx-measure-echo';
    echoBar.id = 'efxMeasureEcho';
    echoBar.setAttribute('aria-hidden', 'true');
    echoBar.innerHTML = '<span class="efx-measure-echo__v" id="efxEchoW">W</span>' +
                        '<span class="efx-measure-echo__x">&times;</span>' +
                        '<span class="efx-measure-echo__v" id="efxEchoH">H</span>';
    wrap.parentNode.insertBefore(echoBar, wrap.nextSibling);
  }
  function syncEcho() {
    if (!echoBar) return;
    var a = document.getElementById('efxEchoW'), b = document.getElementById('efxEchoH');
    if (a && a.textContent !== lblW.textContent) a.textContent = lblW.textContent;
    if (b && b.textContent !== lblH.textContent) b.textContent = lblH.textContent;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════
     10. THE CAPTION (design §3.1) — the one sentence that stops "is this MY fireplace?"
     ══════════════════════════════════════════════════════════════════════════════════ */
  if (!document.getElementById('efxDiagramCaption')) {
    var cap = document.createElement('p');
    cap.className = 'efx-diagram-caption';
    cap.id = 'efxDiagramCaption';
    /* DIRECT-1 (3 IX): one sentence — the deduction rule that used to sit below the fields. The louver note lives in the disclosure now. */
    cap.innerHTML = '<span>The opening — not the doors. We work out the door size.</span>';
    wrap.appendChild(cap);
  }

  /* The inch mark, once, in both places (UX gate 20). */
  if (MARK !== '″') {
    var pill = document.querySelector('.ef-cfg-measure-precision__msg strong');
    if (pill) pill.textContent = pill.textContent.replace(/″/g, MARK);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════
     11. OBSERVERS AND TERMINATION
     ══════════════════════════════════════════════════════════════════════════════════
     Termination: every write is guarded by "desired !== current", so the second pass of any
     observer round is a no-op. The label observers are disconnected across our own write, so
     the wizard's 500 ms redraw and this file cannot ping-pong. No flags, no debounce needed
     for correctness; the 120 ms input debounce exists only so the label feels live. */
  wrap.dataset.lintel = wrap.dataset.lintel || 'proud';

  var pending = false;
  function tick() {
    pending = false;
    echo(); syncBrick(); syncType(); layout(); syncEcho();
  }
  function schedule() {
    if (pending) return;
    pending = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(tick); else setTimeout(tick, 0);
  }

  try {
    new MutationObserver(schedule).observe(rect, { attributes: true, attributeFilter: ['x', 'y', 'width', 'height'] });
    new MutationObserver(schedule).observe(sur, { attributes: true, attributeFilter: ['x', 'y', 'width', 'height', 'fill'] });
    obsW = new MutationObserver(schedule); obsW.observe(lblW, { childList: true, characterData: true, subtree: true });
    obsH = new MutationObserver(schedule); obsH.observe(lblH, { childList: true, characterData: true, subtree: true });
    /* Chapter 1 changes the plate through the tab, which efx-studio-companion.js sets from
       aria-checked. Watch the same signal so the divergence caption keeps up. */
    new MutationObserver(schedule).observe(root, { attributes: true, attributeFilter: ['aria-checked'], subtree: true });
  } catch (e) {}

  var t120 = null;
  function onInput() { clearTimeout(t120); t120 = setTimeout(tick, 120); }
  if (inW) inW.addEventListener('input', onInput);
  if (inH) inH.addEventListener('input', onInput);

  tick();
  window.addEventListener('load', tick, { once: true });
})();

/* ══════════════════════════════════════════════════════════════════════════════════════
   §R8-M8 — THE FRACTION CHIPS.  ROUND-8-SPEC §M8 · lead decision G1 (2026-09-02 19:50 PT).
   ══════════════════════════════════════════════════════════════════════════════════════
   THIS IS THE ONE PLACE THIS FILE WRITES A WIZARD-OWNED NODE, and it is signed off:
   the snippet now ships inputmode="decimal" on #efCfgWidth / #efCfgHeight /
   #efCfgSideHeight, and the iOS decimal keypad has no "/". The chip is the fraction
   route that replaces the missing key.

   The contract, exactly as G1 states it:
     - the write is byte-for-byte what a keypress produces: the same value STRING, then
       'input' and 'change', both bubbling, and NOTHING else on the node - no focus write,
       no attribute, no style, no class, no configurator function call;
     - it never fires on load - only inside a click handler on one of our own buttons;
     - it never touches geometry, opacity or fill of any wizard id (this block reads no
       wizard node but the three inputs; the echo/label path is the existing one - the
       wizard's own 'input' listener redraws and §11's onInput refreshes the label);
     - assets/ef-configurator-v2.js is not opened.

   Disabled until a whole number exists: " 1/8" is not a measurement, and a chip that
   wrote one would hand the wizard a value it must reject. The whole number is whatever
   integer the buyer has already typed; the chip replaces any fraction OR decimal part
   after it, so 36 -> "36 1/8", "36 1/4" -> "36 1/8", "36.5" -> "36 1/8", none -> "36".

   Placement is per FIELD, not one shared row: with the keyboard up at 390 the buyer must
   reach the chips without scrolling past the other dimension. The row lives inside
   .ef-cfg-input-group, so tapping a chip keeps :focus-within and the row does not vanish
   from under the thumb.  Layout: assets/efx-configurator.css §R8-M8. */
(function () {
  'use strict';

  var rows = document.querySelectorAll('.efx-fracrow');
  if (!rows.length) return;

  var WHOLE = /^\s*(\d+)/;
  var FRAC  = /^\s*\d+\s+(\d+\s*\/\s*\d+)\s*$/;

  function whole(v) { var m = WHOLE.exec(v || ''); return m ? m[1] : null; }
  function frac(v)  { var m = FRAC.exec(v || ''); return m ? m[1].replace(/\s+/g, '') : ''; }

  /* Paints OUR OWN buttons only. No wizard node is read or written here. */
  function paint(row, input) {
    var w = whole(input.value), f = w ? frac(input.value) : '';
    var chips = row.querySelectorAll('.efx-fracchip'), i, c;
    for (i = 0; i < chips.length; i++) {
      c = chips[i];
      if (w) c.removeAttribute('disabled'); else c.setAttribute('disabled', '');
      c.setAttribute('aria-pressed', String(w ? c.getAttribute('data-frac') === f : false));
    }
  }

  Array.prototype.forEach.call(rows, function (row) {
    var group = row.closest ? row.closest('.ef-cfg-input-group') : null;
    var input = group && group.querySelector('input[type="text"]');
    if (!input) return;

    paint(row, input);                             /* state only - nothing is dispatched */
    input.addEventListener('input', function () { paint(row, input); });
    input.addEventListener('focus', function () { paint(row, input); });

    /* preventDefault on pointerdown suppresses the compatibility mousedown, so the input
       never blurs and the soft keyboard never drops; 'click' still fires. (Memory:
       setPointerCapture is what kills a click - this does not use it.) */
    row.addEventListener('pointerdown', function (e) {
      if (e.target && e.target.closest && e.target.closest('.efx-fracchip')) e.preventDefault();
    });

    row.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('.efx-fracchip') : null;
      if (!btn || btn.hasAttribute('disabled')) return;
      var w = whole(input.value);
      if (!w) return;                              /* nothing to attach a fraction to */
      var f = btn.getAttribute('data-frac') || '';
      var next = f ? (w + ' ' + f) : w;
      if (input.value !== next) {
        input.value = next;                        /* the keypress result, verbatim */
        input.dispatchEvent(new Event('input',  { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      paint(row, input);
      if (document.activeElement !== input) {
        try { input.focus({ preventScroll: true }); } catch (e2) { input.focus(); }
      }

      /* ROUND-8-SPEC §4 read #4 - "does the chip route get used at all". The push is the
         only thing that can answer it and it originates here or nowhere. M14 installs the
         gtag forward; this is the producer, not the pipe. Window-level, not a node. */
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'cfg_chip_tap',
          field: input.id === 'efCfgWidth' ? 'width' : input.id === 'efCfgHeight' ? 'height' : 'side_height',
          fraction: f || 'none'
        });
      } catch (e3) {}
    });
  });
})();

/* ======================================================================== §R9-P0
   A TAP ON A WIZARD CONTROL MUST NOT BE LOST TO THE REFLOW ITS OWN BLUR CAUSES.

   R9 measured `advancedVia = touch(2nd tap)` on the doors Measurements chapter at
   390 AND 412 (4/4 runs): the customer has to press Next twice. Every other
   chapter advances on one tap.

   MEASURED HERE, not inferred (_reboot/r9-p0/diag-doors.mjs, draft only, 390x844,
   trusted taps, one page load, five stub trials). The event order for a tap on
   #efCfgNext while a measurement field holds focus is

       pointerdown -> touchstart -> pointerup -> touchend -> FOCUSOUT -> click

   The blur lands BEFORE the click, and two focus-driven `display` swaps release
   with it:

     efx-configurator.css §T7-M.6 :6457
        .efx-measure-worksheet:focus-within .ef-cfg-opening-diagram{display:none}
        -> on blur the 253 px plate comes BACK and pushes the nav DOWN
     efx-configurator.css §R8-M8 :7375
        .ef-cfg-input-group:focus-within .efx-fracrow{display:grid}
        -> on blur the 102 px chip row goes AWAY and pulls the nav UP

   #efCfgNext moved 396 -> 560 (+164 px) between touchend and click, so the click
   retargeted to the common ancestor and the button never fired:

     A  baseline, field focused   next 396 -> 560   click @DIV     advanced on tap 2
     B  blurred before the tap    next 396 -> 396   click @NEXT    advanced on tap 1
     C  §R8-M8 fracrow stubbed    next 396 -> 662   click @DIV     advanced on tap 2
     D  §T7-M.6 swap stubbed      next 396 -> 294   click @efCfg   advanced on tap 2
     E  both stubbed              next 396 -> 396   click @NEXT    advanced on tap 1

   NEITHER swap is the culprit on its own - they move the button in OPPOSITE
   directions and partially cancel (+266 and -102 -> +164), which is why C and D
   each still fail. The defect is the reflow itself. So the fix is written as a
   rule about gestures rather than as a patch to either swap: neither stylesheet
   rule is touched, and everything they are gated on (M8 chip parity, Dpx-
   unfocused 0, chips >= 44 px, the keyboard-up echo bar, T7-M.6's plate swap)
   still measures exactly what it measured before.

   THE RULE. While a text field inside #efCfg has focus, a pointerdown on a
   non-text control inside #efCfg is preventDefault()ed. That suppresses the
   compatibility mousedown, so focus does not move, so no :focus-within state
   releases, so nothing reflows between touchend and click - and `click` still
   fires. This is the same mechanism §R8-M8 already relies on for the fraction
   chips, and it deliberately does NOT use setPointerCapture
   (feedback_pointercapture_kills_click).

   FOCUS IS STILL RELEASED, one macrotask after the click, so the soft keyboard
   drops exactly as it did before. The single exception is the §R8-M8 contract
   "focus survives the chip tap": if the control the buyer touched lives in the
   SAME .ef-cfg-input-group as the focused field, focus is left alone. Stated as
   an exception rather than as a list of ids, so a chip added later is covered
   and a nav button added later is not.

   assets/ef-configurator-v2.js is NOT opened, and contains ZERO occurrences of
   the string "blur" (grepped on the draft copy pinned at 826e57318198030e), so
   no wizard behaviour is timed off the blur this defers. Scope is #efCfg: the
   mantel wizard (#efmWrap), the sticky bar and every node outside the doors
   wizard are untouched. preventDefault on `pointerdown` does not cancel
   scrolling - that needs touchstart/touchmove or touch-action - so a drag that
   starts on a button still scrolls the page. */
(function () {
  'use strict';

  function isTextField(el) {
    if (!el || !el.tagName) return false;
    var tag = el.tagName.toLowerCase();
    if (tag === 'textarea') return true;
    if (tag !== 'input') return false;
    var t = (el.type || 'text').toLowerCase();
    return t === 'text' || t === 'number' || t === 'tel' || t === 'email'
        || t === 'search' || t === 'url' || t === 'password';
  }

  /* The focused field, but only if it is the doors wizard's own. */
  function focusedWizardField() {
    var a = document.activeElement;
    if (!isTextField(a) || !a.closest) return null;
    return a.closest('#efCfg') ? a : null;
  }

  var held = null;        /* the field whose focus this gesture is protecting */
  var keepFocus = false;  /* the §R8-M8 chip exception */

  document.addEventListener('pointerdown', function (e) {
    held = null; keepFocus = false;
    var field = focusedWizardField();
    if (!field) return;
    var t = e.target;
    if (!t || !t.closest || !t.closest('#efCfg')) return;
    /* Never interfere with focus moving to another field: the buyer tapping
       Height while Width has focus must still land in Height. */
    if (t.closest('input, textarea, select, label, [contenteditable=""], [contenteditable="true"]')) return;
    var ctrl = t.closest('button, [role="button"], a[href]');
    if (!ctrl) return;
    var fieldGroup = field.closest('.ef-cfg-input-group');
    var ctrlGroup = ctrl.closest('.ef-cfg-input-group');
    keepFocus = !!(fieldGroup && ctrlGroup && fieldGroup === ctrlGroup);
    held = field;
    e.preventDefault();       /* focus stays put; the click still fires */
  }, true);

  document.addEventListener('click', function () {
    var field = held, keep = keepFocus;
    held = null; keepFocus = false;
    if (!field || keep) return;
    /* One macrotask later: the wizard has handled the click and, if it changed
       chapter, the worksheet is already off screen - so the reflow this blur
       causes is no longer under anybody's thumb. */
    setTimeout(function () {
      if (document.activeElement !== field) return;
      try { field.blur(); } catch (err) {}
    }, 0);
  }, false);

  document.addEventListener('pointercancel', function () { held = null; keepFocus = false; }, true);
})();


/* DIRECT-1p2 — the "Check if Square" step (owner 21:30, 2 IX: square check removed) and the tip after it are
   hidden by their heading, so inserting tips above them can never shift the wrong block out of view. */
(function () {
  var steps = document.querySelectorAll('.ef-cfg-htmeasure__content .ef-cfg-htm-step');
  for (var i = 0; i < steps.length; i++) {
    var h = steps[i].querySelector('h4'); if (!h || !/check if square/i.test(h.textContent)) continue;
    steps[i].setAttribute('data-efx-hide', '');
    var n = steps[i].nextElementSibling; if (n && n.classList.contains('ef-cfg-htm-tip')) n.setAttribute('data-efx-hide', '');
  }
}());

/* DIRECT-1u — the lintel picture as a body-level modal. The <details> in the accordion stays the trigger; on
   open, its card is moved into a fixed wrapper on <body> (so no sticky rail or stacking context can sit over
   it) and moved back on close. Closes on ×, backdrop, Escape. Read-only against the wizard. */
(function () {
  var wrap = null, home = null, card = null;
  function close() { if (!wrap) return; if (home && card) home.appendChild(card); wrap.remove(); wrap = null; var ds = document.querySelectorAll('details.efx-lintel-pop[open]'); for (var i = 0; i < ds.length; i++) ds[i].removeAttribute('open'); document.documentElement.classList.remove('efx-lintel-open'); }
  function open(details) {
    card = details.querySelector('.efx-lintel-pop__card'); if (!card) return; home = details;
    wrap = document.createElement('div'); wrap.className = 'efx-lintel-modal'; wrap.setAttribute('role', 'presentation');
    wrap.appendChild(card); document.body.appendChild(wrap); document.documentElement.classList.add('efx-lintel-open');
    var x = card.querySelector('.efx-lintel-pop__close'); if (x) x.focus();
  }
  document.addEventListener('toggle', function (e) { var d = e.target; if (!d || !d.matches || !d.matches('details.efx-lintel-pop')) return; if (d.open) open(d); else close(); }, true);
  document.addEventListener('click', function (e) { var t = e.target; if (!wrap) return; if ((t.closest && t.closest('.efx-lintel-pop__close')) || t === wrap) { e.preventDefault(); close(); } }, true);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
}());

/* DIRECT-1v — mirror the chapter-1 firebox answer onto #efCfg[data-efx-firebox] ("flush" | "hole") so CSS can
   paint the edge fact on the fit-type cards. Read-only against the wizard. */
(function () {
  var cfg = document.getElementById('efCfg'); if (!cfg) return;
  function sync() {
    var g = cfg.querySelector('.ef-cfg-group[data-group="firebox_type"]'); var card = g && g.querySelector('.ef-cfg-card[aria-checked="true"]');
    var v = card ? ((card.getAttribute('data-value') || card.textContent) + '') : '';
    var k = /hole|raised|wall/i.test(v) ? 'hole' : (/flush|hearth/i.test(v) ? 'flush' : '');
    if (cfg.getAttribute('data-efx-firebox') !== k) { if (k) cfg.setAttribute('data-efx-firebox', k); else cfg.removeAttribute('data-efx-firebox'); }
  }
  sync();
  if (window.MutationObserver) new MutationObserver(sync).observe(cfg, { attributes: true, subtree: true, attributeFilter: ['aria-checked'] });
}());

/* DIRECT-1y — "Schedule a 15-minute call" opens two options: Calendly and the phone. On a phone-sized viewport
   the number comes first. Read-only against the wizard; the Calendly link keeps its id and data hook. */
(function () {
  var link = document.getElementById('efxReviewCall'); if (!link) return;
  var tel = (document.querySelector('a[href^="tel:"]') || {}).getAttribute ? document.querySelector('a[href^="tel:"]').getAttribute('href') : 'tel:+19496197824';
  var telText = (tel || '').replace('tel:', '').replace(/^\+1/, '').replace(/(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3') || '(949) 619-7824';
  var menu = document.createElement('div'); menu.className = 'efx-callmenu'; menu.hidden = true; menu.setAttribute('role', 'menu');
  var phoneFirst = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
  var aCal = document.createElement('a'); aCal.className = 'efx-callmenu__item'; aCal.href = link.getAttribute('href'); aCal.target = '_blank'; aCal.rel = 'noopener'; aCal.setAttribute('data-efx-calendly', ''); aCal.setAttribute('role', 'menuitem'); aCal.innerHTML = '<b>Schedule a 15-minute call</b><span>Pick a time \u2014 video or phone, free.</span>';
  var aTel = document.createElement('a'); aTel.className = 'efx-callmenu__item'; aTel.href = tel || 'tel:+19496197824'; aTel.setAttribute('role', 'menuitem'); aTel.innerHTML = '<b>Call ' + telText + '</b><span>A specialist answers, Laguna Hills, CA.</span>';
  if (phoneFirst) { menu.appendChild(aTel); menu.appendChild(aCal); } else { menu.appendChild(aCal); menu.appendChild(aTel); }
  var host = document.createElement('span'); host.className = 'efx-callmenu__host'; link.parentNode.insertBefore(host, link); host.appendChild(link); host.appendChild(menu);
  link.setAttribute('aria-haspopup', 'menu'); link.setAttribute('aria-expanded', 'false');
  /* the trigger itself must not fire the Calendly hook or navigate: the hook moves to the menu item */
  link.removeAttribute('data-efx-calendly'); link.removeAttribute('target'); link.setAttribute('href', '#'); link.setAttribute('role', 'button');
  function open() { menu.hidden = false; link.setAttribute('aria-expanded', 'true'); }
  function close() { menu.hidden = true; link.setAttribute('aria-expanded', 'false'); }
  link.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); if (menu.hidden) open(); else close(); }, true);
  document.addEventListener('click', function (e) { if (!host.contains(e.target)) close(); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
}());

/* DIRECT-1z — depth inside the opening + surround patterns. Everything here is a separate <g> that follows the
   engine's #efCfgOpeningRect; the engine's own nodes are never written. */
(function () {
  var svg = document.getElementById('efCfgOpeningSvg'), rect = document.getElementById('efCfgOpeningRect'), wrap = document.getElementById('efCfgOpeningDiagram');
  if (!svg || !rect || !wrap || document.getElementById('efxDepth')) return;
  var NS = 'http://www.w3.org/2000/svg';
  function node(n, a) { var e = document.createElementNS(NS, n); for (var k in a) e.setAttribute(k, a[k]); return e; }
  var defs = svg.querySelector('defs') || svg.insertBefore(node('defs', {}), svg.firstChild);
  /* patterns: light stacked stone (prefab surround), pale large tile (flush-hearth surround), buff firebrick and
     grey refractory panels (inside), red brick coursing for masonry is the engine's #efBrickPat recoloured in CSS */
  var stone = node('pattern', { id: 'efxStone', patternUnits: 'userSpaceOnUse', width: 48, height: 24 });
  stone.innerHTML = '<rect width="48" height="24" fill="#D9D4CB"/>' +
    '<rect x="0" y="0" width="30" height="11" rx="1" fill="#E6E1D9" stroke="#C4BDB2" stroke-width="0.6"/><rect x="31" y="0" width="17" height="11" rx="1" fill="#DDD8CF" stroke="#C4BDB2" stroke-width="0.6"/>' +
    '<rect x="0" y="12" width="14" height="11" rx="1" fill="#E0DBD2" stroke="#C4BDB2" stroke-width="0.6"/><rect x="15" y="12" width="33" height="11" rx="1" fill="#E9E4DC" stroke="#C4BDB2" stroke-width="0.6"/>';
  var tile = node('pattern', { id: 'efxTile', patternUnits: 'userSpaceOnUse', width: 60, height: 60 });
  tile.innerHTML = '<rect width="60" height="60" fill="#E4E0D8"/><rect x="1" y="1" width="58" height="58" fill="#ECE8E1" stroke="#D3CEC5" stroke-width="0.8"/><path d="M8 50 C 20 34, 34 40, 52 14" stroke="#DED9D0" stroke-width="1.2" fill="none" opacity=".7"/>';
  var fbM = node('pattern', { id: 'efxFbM', patternUnits: 'userSpaceOnUse', width: 36, height: 16 });
  fbM.innerHTML = '<rect width="36" height="16" fill="#C9B58F"/><rect x="0" y="0" width="17" height="7" fill="#D9C7A4" stroke="#B49E78" stroke-width="0.5"/><rect x="18" y="0" width="18" height="7" fill="#D4C19C" stroke="#B49E78" stroke-width="0.5"/><rect x="-9" y="8" width="17" height="7" fill="#D4C19C" stroke="#B49E78" stroke-width="0.5"/><rect x="9" y="8" width="17" height="7" fill="#DCCAA8" stroke="#B49E78" stroke-width="0.5"/><rect x="27" y="8" width="17" height="7" fill="#D2BF9A" stroke="#B49E78" stroke-width="0.5"/>';
  var fbP = node('pattern', { id: 'efxFbP', patternUnits: 'userSpaceOnUse', width: 40, height: 18 });
  fbP.innerHTML = '<rect width="40" height="18" fill="#8E8B86"/><rect x="0" y="0" width="19" height="8" fill="#9A9792" stroke="#75726D" stroke-width="0.5"/><rect x="20" y="0" width="20" height="8" fill="#95928D" stroke="#75726D" stroke-width="0.5"/><rect x="-10" y="9" width="19" height="8" fill="#95928D" stroke="#75726D" stroke-width="0.5"/><rect x="10" y="9" width="19" height="8" fill="#9D9A95" stroke="#75726D" stroke-width="0.5"/><rect x="30" y="9" width="19" height="8" fill="#918E89" stroke="#75726D" stroke-width="0.5"/>';
  var shade = node('linearGradient', { id: 'efxDepthShade', x1: '0', y1: '0', x2: '0', y2: '1' });
  shade.innerHTML = '<stop offset="0" stop-color="#000" stop-opacity=".45"/><stop offset=".35" stop-color="#000" stop-opacity=".08"/><stop offset="1" stop-color="#000" stop-opacity="0"/>';
  [stone, tile, fbM, fbP, shade].forEach(function (p) { defs.appendChild(p); });
  var g = node('g', { id: 'efxDepth' });
  var back = node('rect', { id: 'efxDepthBack', 'class': 'efx-depth__back' });
  var left = node('polygon', { id: 'efxDepthL', 'class': 'efx-depth__side' });
  var right = node('polygon', { id: 'efxDepthR', 'class': 'efx-depth__side' });
  var floor = node('polygon', { id: 'efxDepthFloor', 'class': 'efx-depth__floor' });
  var ceil = node('polygon', { id: 'efxDepthCeil', 'class': 'efx-depth__ceil' });
  var glow = node('rect', { id: 'efxDepthGlow', 'class': 'efx-depth__glow', fill: 'url(#efxDepthShade)' });
  [back, left, right, floor, ceil, glow].forEach(function (e) { g.appendChild(e); });
  rect.parentNode.insertBefore(g, rect.nextSibling);
  function num(el, a) { return parseFloat(el.getAttribute(a)) || 0; }
  function layoutDepth() {
    var x0 = num(rect, 'x'), y0 = num(rect, 'y'), w0 = num(rect, 'width'), h0 = num(rect, 'height'); if (!(w0 > 0 && h0 > 0)) return;
    var d = Math.max(10, Math.min(w0 * 0.16, h0 * 0.22));            /* how far the back wall sits in */
    var bx = x0 + d, by = y0 + d * 0.9, bw = w0 - 2 * d, bh = h0 - d * 0.9 - d * 0.55;   /* back wall: floor is shallower than the ceiling */
    var fy = y0 + h0 - d * 0.55;                                        /* where the floor meets the back wall */
    back.setAttribute('x', bx); back.setAttribute('y', by); back.setAttribute('width', bw); back.setAttribute('height', bh);
    left.setAttribute('points', [x0, y0, bx, by, bx, by + bh, x0, y0 + h0].join(' '));
    right.setAttribute('points', [x0 + w0, y0, bx + bw, by, bx + bw, by + bh, x0 + w0, y0 + h0].join(' '));
    floor.setAttribute('points', [x0, y0 + h0, bx, fy, bx + bw, fy, x0 + w0, y0 + h0].join(' '));
    ceil.setAttribute('points', [x0, y0, x0 + w0, y0, bx + bw, by, bx, by].join(' '));
    glow.setAttribute('x', x0); glow.setAttribute('y', y0); glow.setAttribute('width', w0); glow.setAttribute('height', h0);
  }
  layoutDepth();
  if (window.MutationObserver) new MutationObserver(function () { layoutDepth(); }).observe(rect, { attributes: true, attributeFilter: ['x', 'y', 'width', 'height'] });
}());

/* DIRECT-2d — prefab: the surround stops at the opening. Below it a tiled hearth slab, and a white cover over
   whatever the engine drew further down. Separate nodes that follow #efCfgOpeningRect / #efCfgSurroundRect;
   the engine's own nodes are never written. Shown only under [data-efx-type="prefab"] (CSS). */
(function () {
  var svg = document.getElementById('efCfgOpeningSvg'), rect = document.getElementById('efCfgOpeningRect'), sur = document.getElementById('efCfgSurroundRect');
  if (!svg || !rect || !sur || document.getElementById('efxSlabGroup')) return;
  var NS = 'http://www.w3.org/2000/svg';
  function node(n, a) { var e = document.createElementNS(NS, n); for (var k in a) e.setAttribute(k, a[k]); return e; }
  var g = node('g', { id: 'efxSlabGroup' });
  var cover = node('rect', { id: 'efxSlabCover', 'class': 'efx-slab__cover' });
  var slab = node('rect', { id: 'efxSlab', 'class': 'efx-slab' });
  var lip = node('line', { id: 'efxSlabLip', 'class': 'efx-slab__lip' });
  g.appendChild(cover); g.appendChild(slab); g.appendChild(lip);
  /* DIRECT-2f — last child of the svg, so the slab paints over the box's black bottom band (#efxFlange sits in the
     cue layer, which is appended after the engine's nodes). The dimension line below the surround stays clear
     because the cover ends at the surround's bottom edge. */
  svg.appendChild(g);
  var flange = document.getElementById('efxFlange');
  function num(el, a) { return parseFloat(el.getAttribute(a)) || 0; }
  function lay() {
    var y0 = num(rect, 'y'), h0 = num(rect, 'height'), sx = num(sur, 'x'), sy = num(sur, 'y'), sw = num(sur, 'width'), sh = num(sur, 'height');
    if (!(h0 > 0 && sw > 0)) return;
    var yb = y0 + h0, th = Math.max(8, Math.min(18, h0 * 0.11));
    if (flange) { var fb = num(flange, 'y') + num(flange, 'height') + 1; if (fb > yb && fb - yb < h0 * 0.35) th = Math.max(th, fb - yb); }   /* cover the black band under the opening */
    slab.setAttribute('x', sx - 6); slab.setAttribute('y', yb); slab.setAttribute('width', sw + 12); slab.setAttribute('height', th);
    lip.setAttribute('x1', sx - 6); lip.setAttribute('y1', yb + th); lip.setAttribute('x2', sx + sw + 6); lip.setAttribute('y2', yb + th);
    cover.setAttribute('x', sx - 10); cover.setAttribute('y', yb + th); cover.setAttribute('width', sw + 20); cover.setAttribute('height', Math.max(0, sy + sh - (yb + th)));
  }
  lay();
  if (window.MutationObserver) { var mo = new MutationObserver(lay); mo.observe(rect, { attributes: true, attributeFilter: ['x', 'y', 'width', 'height'] }); mo.observe(sur, { attributes: true, attributeFilter: ['x', 'y', 'width', 'height'] }); if (flange) mo.observe(flange, { attributes: true, attributeFilter: ['x', 'y', 'width', 'height'] }); }
  /* the group must stay last: the cue layer is (re)built by the plate script — re-append after it settles */
  setTimeout(function () { if (svg.lastChild !== g) svg.appendChild(g); lay(); }, 400);
}());

/* DIRECT-2j — the Legend frame drawing dressed as the real door. Separate nodes that follow the engine's rects. */
(function () {
  var svg = document.getElementById('efCfgFrameSvg'); if (!svg || document.getElementById('efxFrameDress')) return;
  var NS = 'http://www.w3.org/2000/svg';
  function node(n, a) { var e = document.createElementNS(NS, n); for (var k in a) e.setAttribute(k, a[k]); return e; }
  var defs = svg.querySelector('defs') || svg.insertBefore(node('defs', {}), svg.firstChild);
  var metal = node('linearGradient', { id: 'efxFrameMetal', x1: '0', y1: '0', x2: '0', y2: '1' });
  metal.innerHTML = '<stop offset="0" stop-color="#3A3A3A"/><stop offset=".08" stop-color="#1C1C1C"/><stop offset=".5" stop-color="#151515"/><stop offset=".92" stop-color="#1F1F1F"/><stop offset="1" stop-color="#050505"/>';
  var mesh = node('pattern', { id: 'efxMesh', patternUnits: 'userSpaceOnUse', width: 3.2, height: 3.2 });
  mesh.innerHTML = '<rect width="3.2" height="3.2" fill="#141414"/><path d="M0 1.6 H3.2 M1.6 0 V3.2" stroke="#3A3A3A" stroke-width="0.55"/><circle cx="1.6" cy="1.6" r="0.35" fill="#0A0A0A"/>';
  var handleG = node('linearGradient', { id: 'efxHandleMetal', x1: '0', y1: '0', x2: '1', y2: '0' });
  handleG.innerHTML = '<stop offset="0" stop-color="#6E6E6E"/><stop offset=".45" stop-color="#2A2A2A"/><stop offset="1" stop-color="#5A5A5A"/>';
  defs.appendChild(metal); defs.appendChild(mesh); defs.appendChild(handleG);
  var dress = node('g', { id: 'efxFrameDress' });
  var leafL = node('rect', { 'class': 'efx-frame__leaf' }), leafR = node('rect', { 'class': 'efx-frame__leaf' });
  var hL = node('rect', { 'class': 'efx-frame__handle', rx: 1.6 }), hR = node('rect', { 'class': 'efx-frame__handle', rx: 1.6 });
  var hL2 = node('rect', { 'class': 'efx-frame__handle-stub' }), hR2 = node('rect', { 'class': 'efx-frame__handle-stub' });
  var glassL = document.getElementById('efGlassL'), glassR = document.getElementById('efGlassR'), div = document.getElementById('efDivider');
  if (!glassL || !glassR || !div) return;
  /* leaf bezels sit under the glass; handles over it */
  glassL.parentNode.insertBefore(leafL, glassL); glassL.parentNode.insertBefore(leafR, glassL);
  [hL2, hR2, hL, hR].forEach(function (e) { dress.appendChild(e); }); svg.appendChild(dress);
  function num(el, a) { return parseFloat(el.getAttribute(a)) || 0; }
  function lay() {
    var lx = num(glassL, 'x'), ly = num(glassL, 'y'), lw = num(glassL, 'width'), lh = num(glassL, 'height');
    var rx = num(glassR, 'x'), rw = num(glassR, 'width');
    if (!(lw > 0 && lh > 0)) return;
    var b = 2.2;
    leafL.setAttribute('x', lx - b); leafL.setAttribute('y', ly - b); leafL.setAttribute('width', lw + 2 * b); leafL.setAttribute('height', lh + 2 * b);
    leafR.setAttribute('x', rx - b); leafR.setAttribute('y', ly - b); leafR.setAttribute('width', rw + 2 * b); leafR.setAttribute('height', lh + 2 * b);
    var cy = ly + lh / 2, hh = Math.min(30, lh * 0.22), hw = 3.2, off = 5;
    hL.setAttribute('x', lx + lw - off - hw); hL.setAttribute('y', cy - hh / 2); hL.setAttribute('width', hw); hL.setAttribute('height', hh);
    hR.setAttribute('x', rx + off); hR.setAttribute('y', cy - hh / 2); hR.setAttribute('width', hw); hR.setAttribute('height', hh);
    [[hL2, lx + lw - off - hw - 1.2], [hR2, rx + off - 1.2]].forEach(function (p) { p[0].setAttribute('x', p[1]); p[0].setAttribute('y', cy - hh / 2 + 3); p[0].setAttribute('width', hw + 2.4); p[0].setAttribute('height', 3); });
  }
  lay();
  if (window.MutationObserver) { var mo = new MutationObserver(lay); [glassL, glassR, div].forEach(function (el) { mo.observe(el, { attributes: true, attributeFilter: ['x', 'y', 'width', 'height'] }); }); }
}());

/* DIRECT-2m — (a) the examples lightbox is a child of the card; when it opens it is moved to <body> so no ancestor
   stacking context (the sticky step bar's) can sit above it. (b) Desktop "Take Photo": a QR hands the photo from the
   phone to this session through the ef-photo-relay worker; if the relay does not answer, the file picker opens. */
(function () {
  var lb = document.querySelector('.ef-cfg-ai-card__lightbox');
  if (lb && window.MutationObserver) {
    new MutationObserver(function () { if (lb.getAttribute('aria-hidden') === 'false' && lb.parentNode !== document.body) document.body.appendChild(lb); }).observe(lb, { attributes: true, attributeFilter: ['aria-hidden'] });
  }
  var RELAY = window.EFX_PHOTO_RELAY || 'https://ef-photo-relay.danielzajacus.workers.dev';
  /* DIRECT-2p — the hover/fine pair was false on some Windows machines and the click fell through to the picker */
  var desktop = window.matchMedia('(min-width: 1024px)').matches && !window.matchMedia('(pointer: coarse)').matches;
  if (!desktop) return;
  function openPicker(inputId) { var inp = document.getElementById(inputId); if (!inp) return; inp.removeAttribute('capture'); inp.click(); }
  function loadQr(cb) { if (window.QRCode) return cb(); var s = document.createElement('script'); s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js'; s.async = true; s.onload = cb; s.onerror = function () { cb(new Error('qr')); }; document.head.appendChild(s); }
  function inject(inputId, blob) {
    var inp = document.getElementById(inputId); if (!inp) return false;
    try { var f = new File([blob], 'fireplace-' + Date.now() + '.jpg', { type: blob.type || 'image/jpeg' }); var dt = new DataTransfer(); dt.items.add(f); inp.files = dt.files; inp.dispatchEvent(new Event('change', { bubbles: true })); return true; } catch (e) { return false; }
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ef-cam]'); if (!btn) return;
    var inputId = btn.getAttribute('data-ef-cam') || 'efCfgAiFile';
    e.preventDefault(); e.stopPropagation();
    var ctrl = { closed: false }, timer = null;
    var wrap = document.createElement('div'); wrap.className = 'efx-lintel-modal efx-qrmodal'; wrap.setAttribute('role', 'dialog'); wrap.setAttribute('aria-label', 'Take the photo on your phone');
    wrap.innerHTML = '<div class="efx-lintel-pop__card efx-qrmodal__card"><button type="button" class="efx-lintel-pop__close" aria-label="Close">\u00d7</button>' +
      '<div class="efx-qrmodal__body"><div class="efx-qrmodal__qr" data-efx-qr><span class="efx-qrmodal__wait">Connecting\u2026</span></div>' +
      '<div class="efx-qrmodal__text"><p class="efx-qrmodal__title">Take the photo on your phone</p><p class="efx-qrmodal__sub">Scan the code with your phone\u2019s camera. Take one photo of the whole opening; it appears here by itself.</p>' +
      '<p class="efx-qrmodal__state" data-efx-qr-state>Waiting for your phone\u2026</p><button type="button" class="efx-link efx-qrmodal__alt" data-efx-qr-file>Or choose a file from this computer</button></div></div></div>';
    document.body.appendChild(wrap);
    function close() { if (ctrl.closed) return; ctrl.closed = true; clearTimeout(timer); wrap.remove(); document.removeEventListener('keydown', onKey); }
    function onKey(ev) { if (ev.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    wrap.addEventListener('click', function (ev) { if (ev.target === wrap || ev.target.closest('.efx-lintel-pop__close')) close(); });
    wrap.querySelector('[data-efx-qr-file]').addEventListener('click', function () { close(); openPicker(inputId); });
    var stateEl = wrap.querySelector('[data-efx-qr-state]'), qrEl = wrap.querySelector('[data-efx-qr]');
    var fallback = function () { close(); openPicker(inputId); };
    var ac = ('AbortController' in window) ? new AbortController() : null; if (ac) setTimeout(function () { ac.abort(); }, 6000);
    fetch(RELAY + '/session', { method: 'POST', signal: ac ? ac.signal : undefined }).then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); }).then(function (s) {
      if (ctrl.closed) return;
      loadQr(function (err) {
        if (err || ctrl.closed) { if (!ctrl.closed) fallback(); return; }
        qrEl.innerHTML = ''; new QRCode(qrEl, { text: s.phone, width: 168, height: 168, colorDark: '#121212', colorLight: '#ffffff', correctLevel: QRCode.CorrectLevel.M });
      });
      var started = Date.now();
      (function poll() {
        if (ctrl.closed) return;
        if (Date.now() - started > 6 * 60 * 1000) { stateEl.textContent = 'The code expired. Close and press Take Photo again.'; return; }
        fetch(RELAY + '/photo/' + s.token).then(function (r) { if (r.status === 200) return r.blob(); if (r.status === 410) throw new Error('expired'); return null; }).then(function (blob) {
          if (ctrl.closed) return;
          if (blob) { stateEl.textContent = 'Photo received \u2014 identifying\u2026'; var ok = inject(inputId, blob); setTimeout(close, ok ? 500 : 0); if (!ok) openPicker(inputId); return; }
          timer = setTimeout(poll, 2000);
        }).catch(function () { if (!ctrl.closed) { stateEl.textContent = 'The code expired. Close and press Take Photo again.'; } });
      }());
    }).catch(function () { if (!ctrl.closed) fallback(); });
  }, true);
}());

/* DIRECT-2r — phone: a running Total bar right under the form, mirrored from the companion (hidden ≤1023). */
(function () {
  if (!window.matchMedia('(max-width: 1023px)').matches) return;
  var cfg = document.getElementById('efCfg'), src = document.querySelector('[data-efx-companion-total]'), lab = document.querySelector('[data-efx-companion-label]');
  if (!cfg || !src) return;
  var bar = document.createElement('div'); bar.className = 'efx-mtotal'; bar.setAttribute('aria-live', 'polite');
  bar.innerHTML = '<span class="efx-mtotal__lab"></span><b class="efx-mtotal__val"></b><span class="efx-mtotal__sub">Pay over time with Shop Pay at checkout</span>';
  cfg.parentNode.insertBefore(bar, cfg.nextSibling);
  var v = bar.querySelector('.efx-mtotal__val'), l = bar.querySelector('.efx-mtotal__lab');
  function paint() { v.textContent = (src.textContent || '').trim(); l.textContent = ((lab && lab.textContent) || 'Total').trim() || 'Total'; var rv = document.getElementById('efCfgStepReview'); bar.hidden = !!(rv && rv.classList.contains('is-visible')); }
  paint();
  if (window.MutationObserver) { new MutationObserver(paint).observe(src, { childList: true, characterData: true, subtree: true }); if (lab) new MutationObserver(paint).observe(lab, { childList: true, characterData: true, subtree: true }); var rv = document.getElementById('efCfgStepReview'); if (rv) new MutationObserver(paint).observe(rv, { attributes: true, attributeFilter: ['class'] }); }
}());
